import {
  BRAIN_SEARCH_SOURCE_MIN_WIDTH,
  BrainSearchMessageSourceSkeleton,
} from "@/brain-search/internal/BrainSearchMessageInternalSource"
import { BrainSearchMessageExternalSourceFragment$key } from "@/brain-search/internal/__generated__/BrainSearchMessageExternalSourceFragment.graphql"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import { DiscoText } from "@disco-ui"
import DiscoContainerButton from "@disco-ui/button/DiscoContainerButton"
import { TestIDProps } from "@utils/typeUtils"
import { getFaviconUrl } from "@utils/url/urlUtils"
import classNames from "classnames"
import { graphql, useFragment } from "react-relay"

interface Props extends TestIDProps {
  brainSearchMessageExternalSourceKey: BrainSearchMessageExternalSourceFragment$key
}

function BrainSearchMessageExternalSource({
  brainSearchMessageExternalSourceKey,
}: Props) {
  const classes = useStyles()

  const source = useFragment<BrainSearchMessageExternalSourceFragment$key>(
    graphql`
      fragment BrainSearchMessageExternalSourceFragment on BrainSearchMessageExternalSource {
        id
        entity
        url
        owner
        title
        score
      }
    `,
    brainSearchMessageExternalSourceKey
  )
  if (!source || !source.title || !source.owner) {
    return <BrainSearchMessageSourceSkeleton />
  }

  const renderExternalSourceImage = () => {
    const faviconUrl = getFaviconUrl(source.url)
    if (!faviconUrl) {
      console.error(`Invalid URL for External Source: '${source.url}'`)
      return <div className={classNames(classes.favIcon, classes.fallbackAvatar)} />
    }
    return <img alt={""} src={faviconUrl} className={classes.favIcon} />
  }

  return (
    <DiscoContainerButton
      key={source.id}
      className={classNames(classes.source, classes.fadeIn)}
      onClick={source.url ? () => handleClickExternalSource(source.url) : undefined}
    >
      <div className={classes.topSource}>
        <DiscoText variant={"body-xs-700"} color={"text.primary"} truncateText={3}>
          {source.title}
        </DiscoText>
      </div>
      <div className={classes.bottomSource}>
        <div className={classes.sourceContent}>
          {renderExternalSourceImage()}
          <DiscoText variant={"body-xs"} color={"text.secondary"} truncateText={1}>
            {source.owner}
          </DiscoText>
        </div>
      </div>
    </DiscoContainerButton>
  )

  function handleClickExternalSource(url: string) {
    window.open(url, "_blank")
  }
}

const useStyles = makeUseStyles((theme) => ({
  source: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.measure.borderRadius.big,
    backgroundColor: theme.palette.groovy.neutral[100],
    border: `1px solid ${theme.palette.groovy.neutral[100]}`,
    minHeight: "96px",
    minWidth: BRAIN_SEARCH_SOURCE_MIN_WIDTH,

    "&:hover": {
      borderColor: theme.palette.groovy.neutral[300],
      backgroundColor: theme.palette.groovy.neutral[200],
      boxShadow:
        "0px 2px 4px 0px rgba(63, 63, 70, 0.04), 0px 6px 18px 0px rgba(63, 63, 70, 0.08)",
    },
  },
  topSource: {
    width: "100%",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexDirection: "row",
    display: "flex",
    flex: 1,
    gap: theme.spacing(1),
  },
  bottomSource: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  sourceContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    width: "100%",
  },
  favIcon: {
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    background:
      "radial-gradient(100% 100% at 50% 0%, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%), var(--Colors-blue-600, #4D47DC)",
  },
  fallbackAvatar: {
    backgroundColor: theme.palette.grey[200],
  },
  fadeIn: {
    animation: "$fadeIn 1s",
  },
  // eslint-disable-next-line local-rules/disco-unused-classes
  "@keyframes fadeIn": {
    "0%": { opacity: 0 },
    "100%": { opacity: 1 },
  },
}))

export default BrainSearchMessageExternalSource
